import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { customPushService } from '../../services/customPushService';
import { ICustomPushApiKey, ICustomPushApiKeyCreateResponse } from '../../types/customPush';
import ApiKeyDialog from './ApiKeyDialog';
import ApiKeyCreatedDialog from './ApiKeyCreatedDialog';

const isNotYetValid = (apiKey: ICustomPushApiKey): boolean =>
  !!apiKey.validFrom && new Date(apiKey.validFrom) > new Date();

const isExpired = (apiKey: ICustomPushApiKey): boolean =>
  !!apiKey.validUntil && new Date(apiKey.validUntil) < new Date();

const ApiKeysTab: React.FC = () => {
  const [apiKeys, setApiKeys] = useState<ICustomPushApiKey[]>([]);
  const [notificationTypeOptions, setNotificationTypeOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState<ICustomPushApiKey | null>(null);
  const [createdResult, setCreatedResult] = useState<ICustomPushApiKeyCreateResponse | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [keys, types] = await Promise.all([
        customPushService.getApiKeys(),
        customPushService.getNotificationTypes(),
      ]);
      setApiKeys(keys);
      setNotificationTypeOptions(types.filter((t) => t.isActive).map((t) => t.notificationType));
    } catch (error) {
      console.error('Error loading API keys:', error);
      setApiKeys([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleNew = () => {
    setSelected(null);
    setDialogOpen(true);
  };

  const handleEdit = (apiKey: ICustomPushApiKey) => {
    setSelected(apiKey);
    setDialogOpen(true);
  };

  const handleDeactivate = async (apiKey: ICustomPushApiKey) => {
    if (!apiKey.apiKeyId) return;
    if (!window.confirm(`Deactivate API key "${apiKey.name}"? Any integration using it will stop working.`)) return;
    try {
      await customPushService.deactivateApiKey(apiKey.apiKeyId);
      loadData();
    } catch (error) {
      console.error('Error deactivating API key:', error);
      alert('Network error. Try again later');
    }
  };

  const handleDelete = async (apiKey: ICustomPushApiKey) => {
    if (!apiKey.apiKeyId) return;
    if (
      !window.confirm(
        `Permanently delete API key "${apiKey.name}"? This cannot be undone. Any integration using it will stop working immediately.`
      )
    )
      return;
    try {
      await customPushService.deleteApiKey(apiKey.apiKeyId);
      loadData();
    } catch (error) {
      console.error('Error deleting API key:', error);
      alert('Network error. Try again later');
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Typography variant="h6" sx={{ fontWeight: 500 }}>
          API Keys
        </Typography>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleNew}>
          New API Key
        </Button>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" p={6}>
          <CircularProgress size={48} />
        </Box>
      ) : (
        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: 700 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Key</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Notification Types</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Validity</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {apiKeys.map((apiKey) => (
                  <TableRow key={apiKey.apiKeyId} hover>
                    <TableCell sx={{ wordBreak: 'break-word' }}>
                      <Typography variant="body2" fontWeight={500}>{apiKey.name}</Typography>
                      {apiKey.description && (
                        <Typography variant="caption" color="text.secondary">{apiKey.description}</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{apiKey.keyPrefix}…</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 260 }}>
                        {apiKey.notificationTypes.map((type) => (
                          <Chip key={type} label={type} size="small" variant="outlined" />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {apiKey.validFrom || apiKey.validUntil ? (
                        <Box>
                          <Typography variant="caption" display="block" color="text.secondary">
                            From: {apiKey.validFrom ? new Date(apiKey.validFrom).toLocaleString() : 'anytime'}
                          </Typography>
                          <Typography variant="caption" display="block" color="text.secondary">
                            Until: {apiKey.validUntil ? new Date(apiKey.validUntil).toLocaleString() : 'no expiry'}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          No restriction
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                        <Chip
                          label={apiKey.isActive ? 'Active' : 'Inactive'}
                          color={apiKey.isActive ? 'success' : 'default'}
                          size="small"
                        />
                        {apiKey.isActive && isNotYetValid(apiKey) && (
                          <Chip label="Not yet valid" color="warning" size="small" variant="outlined" />
                        )}
                        {apiKey.isActive && isExpired(apiKey) && (
                          <Chip label="Expired" color="warning" size="small" variant="outlined" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Tooltip title="Edit">
                          <IconButton size="small" color="primary" onClick={() => handleEdit(apiKey)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {apiKey.isActive && (
                          <Tooltip title="Deactivate">
                            <IconButton size="small" color="warning" onClick={() => handleDeactivate(apiKey)}>
                              <BlockIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Delete permanently">
                          <IconButton size="small" color="error" onClick={() => handleDelete(apiKey)}>
                            <DeleteForeverIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {apiKeys.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                        No API keys created yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <ApiKeyDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        apiKey={selected}
        notificationTypeOptions={notificationTypeOptions}
        onSaved={loadData}
        onCreated={setCreatedResult}
      />

      <ApiKeyCreatedDialog
        open={!!createdResult}
        onClose={() => setCreatedResult(null)}
        result={createdResult}
      />
    </Box>
  );
};

export default ApiKeysTab;
